import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Authenticated from '../../layout/Authenticated';
import { ArrowLeft, Save, AlertCircle, CheckCircle2, IndianRupee, RotateCcw } from 'lucide-react';
import axiosInstance from '../../api/axiosInstance';

const UpdateSalesReturnStatus = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [salesReturn, setSalesReturn] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        return_status: 'PENDING',
        refund_amount: '',
        refund_method: '',
        refund_date: '',
        notes: ''
    });
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');

    const statusOptions = [
        { value: 'PENDING', label: 'Pending' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'PROCESSED', label: 'Processed' },
        { value: 'REFUNDED', label: 'Refunded' }
    ];

    const refundMethods = [
        { value: 'CASH', label: 'Cash' },
        { value: 'ONLINE', label: 'Online Transfer' },
        { value: 'ADJUSTMENT', label: 'Account Adjustment' },
        { value: 'CREDIT_NOTE', label: 'Credit Note' }
    ];

    useEffect(() => {
        fetchSalesReturnDetails();
    }, [id]);

    const fetchSalesReturnDetails = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/sales-returns/${id}`);
            const data = response.data;
            setSalesReturn(data);

            setFormData({
                return_status: data.return_status,
                refund_amount: data.refund_amount || '',
                refund_method: data.refund_method || '',
                refund_date: data.refund_date ? data.refund_date.split('T')[0] : '',
                notes: data.notes || ''
            });
        } catch (error) {
            console.error('Error fetching sales return details:', error);
            showAlertMessage('Error loading return details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showAlertMessage = (message, type = 'success') => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const submitData = {
                return_status: formData.return_status,
                refund_amount: formData.refund_amount ? parseFloat(formData.refund_amount) : null,
                refund_method: formData.refund_method || null,
                refund_date: formData.refund_date || null,
                notes: formData.notes
            };

            await axiosInstance.put(`/sales-returns/${id}/status`, submitData);
            showAlertMessage('Sales return status updated successfully!', 'success');

            setTimeout(() => {
                navigate(`/sales-return-list`);
            }, 2000);

        } catch (error) {
            console.error('Error updating sales return status:', error);
            const errorMessage = error.response?.data?.error || 'Error updating sales return status';
            showAlertMessage(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Authenticated>
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
            </Authenticated>
        );
    }

    if (!salesReturn) {
        return (
            <Authenticated>
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                        <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Sales return not found</h3>
                    </div>
                </div>
            </Authenticated>
        );
    }

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Alert */}
                    {showAlert && (
                        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${alertType === 'success' ? 'bg-green-500' : 'bg-red-500'
                            } text-white max-w-md`}>
                            <div className="flex items-center justify-between">
                                <span>{alertMessage}</span>
                                <button
                                    onClick={() => setShowAlert(false)}
                                    className="text-white hover:text-gray-200 ml-4"
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate(`/sales-return-list`)}
                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div className="bg-teal-600 p-2 rounded-lg">
                                <CheckCircle2 className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Update Return Status</h1>
                                <p className="text-sm text-gray-600 mt-1">Return #{salesReturn.id} - {salesReturn.customer_name}</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Status Update Form */}
                        <div className="lg:col-span-2">
                            <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <h2 className="text-lg font-semibold text-gray-900 mb-6">Status Information</h2>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Return Status <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.return_status}
                                            onChange={(e) => handleInputChange('return_status', e.target.value)}
                                            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                        >
                                            {statusOptions.map((status) => (
                                                <option key={status.value} value={status.value}>
                                                    {status.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {(formData.return_status === 'REFUNDED' || formData.return_status === 'PROCESSED') && (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Refund Amount
                                                    </label>
                                                    <div className="relative">
                                                        <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max={salesReturn.total_return_amt}
                                                            value={formData.refund_amount}
                                                            onChange={(e) => handleInputChange('refund_amount', e.target.value)}
                                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Max: ₹{parseFloat(salesReturn.total_return_amt).toFixed(2)}
                                                    </p>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Refund Method
                                                    </label>
                                                    <select
                                                        value={formData.refund_method}
                                                        onChange={(e) => handleInputChange('refund_method', e.target.value)}
                                                        className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                                    >
                                                        <option value="">Select Method</option>
                                                        {refundMethods.map((method) => (
                                                            <option key={method.value} value={method.value}>
                                                                {method.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Refund Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={formData.refund_date}
                                                    onChange={(e) => handleInputChange('refund_date', e.target.value)}
                                                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Additional Notes
                                        </label>
                                        <textarea
                                            value={formData.notes}
                                            onChange={(e) => handleInputChange('notes', e.target.value)}
                                            rows={4}
                                            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                                            placeholder="Add any additional notes about the status update..."
                                        />
                                    </div>

                                    {formData.return_status === 'APPROVED' && (
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-start">
                                                <CheckCircle2 className="text-green-500 mt-0.5 mr-3" size={16} />
                                                <div className="text-sm text-green-700">
                                                    <p className="font-medium">Approving this return</p>
                                                    <p>Intact medicines will be automatically restocked and available for sale.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {formData.return_status === 'REJECTED' && (
                                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                            <div className="flex items-start">
                                                <AlertCircle className="text-red-500 mt-0.5 mr-3" size={16} />
                                                <div className="text-sm text-red-700">
                                                    <p className="font-medium">Important: Rejecting this return</p>
                                                    <p>No stock restoration will occur and no refund will be processed.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-start items-center space-x-3 mt-8">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`px-6 py-2.5 rounded-lg flex items-center ${isSubmitting
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg'
                                            } transition-all duration-200`}
                                    >
                                        <Save size={18} className="mr-2" />
                                        {isSubmitting ? 'Updating...' : 'Update Status'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/sales-return-list`)}
                                        className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Return Summary */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Return Summary</h3>

                            <div className="space-y-4">
                                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                                    <span className="text-sm text-gray-600">Return ID</span>
                                    <span className="font-medium">#{salesReturn.id}</span>
                                </div>

                                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                                    <span className="text-sm text-gray-600">Customer</span>
                                    <span className="font-medium text-right">{salesReturn.customer_name}</span>
                                </div>

                                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                                    <span className="text-sm text-gray-600">Original Invoice</span>
                                    <span className="font-medium">#{salesReturn.original_invoice_id}</span>
                                </div>

                                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                                    <span className="text-sm text-gray-600">Items Count</span>
                                    <span className="font-medium">{salesReturn.returnitems?.length || 0}</span>
                                </div>

                                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                                    <span className="text-sm text-gray-600">Return Date</span>
                                    <span className="font-medium">
                                        {new Date(salesReturn.return_date).toLocaleDateString()}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                                    <span className="text-sm text-gray-600">Intact Items</span>
                                    <span className="font-medium text-green-600">
                                        {salesReturn.returnitems?.filter(item => item.is_medicine_intact).length || 0}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center text-lg font-semibold text-teal-600">
                                    <span>Total Amount</span>
                                    <div className="flex items-center">
                                        <IndianRupee size={18} className="mr-1" />
                                        ₹{parseFloat(salesReturn.total_return_amt).toFixed(2)}
                                    </div>
                                </div>

                                {parseFloat(salesReturn.refund_amount) > 0 && (
                                    <div className="flex justify-between items-center text-green-600">
                                        <span className="text-sm">Current Refund</span>
                                        <div className="flex items-center">
                                            <IndianRupee size={14} className="mr-1" />
                                            ₹{parseFloat(salesReturn.refund_amount).toFixed(2)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Authenticated>
    );
};

export default UpdateSalesReturnStatus;